/**
 * BIRHAUS Utilities
 * 
 * Shared utility functions for all BIRHAUS components
 */

/**
 * Type for classname values that can be merged
 */
export type ClassValue = 
  | string 
  | number 
  | boolean 
  | undefined 
  | null 
  | ClassValue[] 
  | Record<string, boolean>

/**
 * Utility function to merge classnames (optimized clsx/cn alternative)
 * Handles strings, arrays, objects, and falsy values
 */
export function cn(...inputs: ClassValue[]): string {
  const classes: string[] = []
  
  for (const input of inputs) {
    if (!input) continue
    
    if (typeof input === 'string') {
      classes.push(input)
    } else if (typeof input === 'number') {
      classes.push(String(input))
    } else if (Array.isArray(input)) {
      const nested = cn(...input)
      if (nested) classes.push(nested)
    } else if (typeof input === 'object') {
      for (const [key, value] of Object.entries(input)) {
        if (value) classes.push(key)
      }
    }
  }
  
  return classes.join(' ')
}

/**
 * Utility to conditionally apply classes based on variants
 */
export function cva(
  base: string,
  options?: {
    variants?: Record<string, Record<string, string>>
    compoundVariants?: Array<{
      [key: string]: any
      class: string
    }>
    defaultVariants?: Record<string, any>
  }
) {
  return (props?: Record<string, any>) => {
    const classes = [base]
    
    if (!options || !props) return cn(...classes)
    
    // Apply variant classes
    if (options.variants) {
      for (const [key, variants] of Object.entries(options.variants)) {
        const variant = props[key] ?? options.defaultVariants?.[key]
        if (variant && variants[variant]) {
          classes.push(variants[variant])
        }
      }
    }
    
    // Apply compound variant classes
    if (options.compoundVariants) {
      for (const compound of options.compoundVariants) {
        const { class: className, ...conditions } = compound
        let matches = true
        
        for (const [key, value] of Object.entries(conditions)) {
          if (props[key] !== value) {
            matches = false
            break
          }
        }
        
        if (matches) {
          classes.push(className)
        }
      }
    }
    
    return cn(...classes)
  }
}